// Comment Modal - Modal for adding comments with optional verse links
import { getVerse, getVerses } from '../bible.js';
import { showVerseSelector } from './verseSelector.js';

export class CommentModal {
  constructor(postId, onComment, existingComment = null) {
    this.postId = postId;
    this.onComment = onComment;
    this.existingComment = existingComment;
    this.linkedVerses = existingComment?.linkedVerses || [];
    this.isEditMode = !!existingComment;
  }

  open() {
    const modal = document.createElement('div');
    modal.className = 'comment-modal-overlay';
    
    const initialText = this.existingComment?.text || '';
    const modalTitle = this.isEditMode ? '✏️ Modifier le commentaire' : '💬 Ajouter un commentaire';
    const submitLabel = this.isEditMode ? '✓ Modifier' : '✓ Publier';
    
    modal.innerHTML = `
      <div class="comment-modal">
        <div class="modal-header">
          <h2>${modalTitle}</h2>
          <button class="close-btn">✕</button>
        </div>

        <div class="modal-content">
          <div id="linked-verses-container" class="linked-verses-container" style="display: none;">
            <h4>Versets liés :</h4>
            <div id="linked-verses-list" class="linked-verses-list"></div>
          </div>

          <textarea 
            id="comment-text" 
            placeholder="Écrive un commentaire..."
            maxlength="1000"
            rows="5"
          >${initialText}</textarea>

          <div class="comment-footer">
            <div class="char-count">
              <span id="char-count">${initialText.length}</span>/1000
            </div>
            
            <div class="action-buttons">
              <button id="add-verse-btn" class="btn-secondary">
                📖 Ajouter un verset
              </button>
              <button id="submit-comment-btn" class="btn-primary" ${initialText.trim().length === 0 ? 'disabled' : ''}>
                ${submitLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modalElement = modal;
    
    // Display existing linked verses if in edit mode
    if (this.linkedVerses.length > 0) {
      this.displayLinkedVerses();
    }
    
    this.attachEventListeners();
  }

  attachEventListeners() {
    const closeBtn = this.modalElement.querySelector('.close-btn');
    const addVerseBtn = this.modalElement.querySelector('#add-verse-btn');
    const submitBtn = this.modalElement.querySelector('#submit-comment-btn');
    const textArea = this.modalElement.querySelector('#comment-text');
    const charCount = this.modalElement.querySelector('#char-count');

    closeBtn.addEventListener('click', () => this.close());
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.close();
    });

    textArea.addEventListener('input', (e) => {
      charCount.textContent = e.target.value.length;
      submitBtn.disabled = e.target.value.trim().length === 0;
    });

    addVerseBtn.addEventListener('click', () => {
      showVerseSelector(async (verse) => {
        this.addLinkedVerse(verse);
      });
    });

    submitBtn.addEventListener('click', () => {
      const commentText = textArea.value.trim();
      if (commentText.length > 0) {
        this.onComment({
          text: commentText,
          linkedVerses: this.linkedVerses
        });
        this.close();
      }
    });
  }

  async addLinkedVerse(verse) {
    this.linkedVerses.push(verse);
    this.displayLinkedVerses();
  }

  async displayLinkedVerses() {
    const container = this.modalElement.querySelector('#linked-verses-container');
    const list = this.modalElement.querySelector('#linked-verses-list');

    if (this.linkedVerses.length === 0) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'block';
    list.innerHTML = '';

    for (let i = 0; i < this.linkedVerses.length; i++) {
      const verseRef = this.linkedVerses[i];
      try {
        const version = localStorage.getItem('user_preferred_lang') || 'french';
        let verseText = '';
        
        if (verseRef.verseStart === verseRef.verseEnd) {
          verseText = await getVerse(version, verseRef.book, verseRef.chapter, verseRef.verseStart);
        } else {
          const verses = await getVerses(version, verseRef.book, verseRef.chapter, verseRef.verseStart, verseRef.verseEnd);
          verseText = verses.map(v => `${v.text}`).join(' ');
        }

        const verseElement = document.createElement('div');
        verseElement.className = 'linked-verse-pill';
        verseElement.innerHTML = `
          <div class="verse-ref">
            ${verseRef.bookName} ${verseRef.chapter}:${verseRef.verseStart}${verseRef.verseStart !== verseRef.verseEnd ? '-' + verseRef.verseEnd : ''}
          </div>
          <div class="verse-text">${verseText.substring(0, 100)}...</div>
          <button class="remove-verse-btn" data-index="${i}">
            ✕
          </button>
        `;
        list.appendChild(verseElement);
      } catch (error) {
        console.error('Erreur chargement verset:', error);
      }
    }

    list.querySelectorAll('.remove-verse-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.index);
        this.linkedVerses.splice(index, 1);
        this.displayLinkedVerses();
      });
    });
  }

  close() {
    this.modalElement.remove();
  }
}
